/**
 * Reusable drag-and-drop reordering for lists using native HTML5 DnD (no library).
 *
 * Usage:
 *   const { getItemProps, isDragging } = useListDnd({ items, getId, onReorder });
 *   ...
 *   {items.map((it) => (
 *     <div {...getItemProps(it)} key={getId(it)}>...</div>
 *   ))}
 *
 * `onReorder(newList, meta)` is called after the user drops.
 *   • newList: the reordered array
 *   • meta:    { movedId, targetId, movedItem, targetItem } — useful for cross-group moves
 *              where the parent needs to detect that item moved from one group to another
 *              (e.g. change folder / tag).
 */
import { useCallback, useState } from "react";

export default function useListDnd({ items, getId, onReorder }) {
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const handleDragStart = useCallback((id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(id)); } catch {}
  }, []);

  const handleDragOver = useCallback((id) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === id) return;
    setOverId(id);
  }, [dragId]);

  const handleDrop = useCallback((targetId) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setOverId(null); return; }
    const from = items.findIndex((x) => getId(x) === dragId);
    const to = items.findIndex((x) => getId(x) === targetId);
    if (from < 0 || to < 0) { setOverId(null); setDragId(null); return; }
    const movedItem = items[from];
    const targetItem = items[to];
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next, { movedId: dragId, targetId, movedItem, targetItem });
    setDragId(null);
    setOverId(null);
  }, [dragId, items, getId, onReorder]);

  const handleDragEnd = useCallback(() => {
    setDragId(null);
    setOverId(null);
  }, []);

  const getItemProps = useCallback((it) => {
    const id = getId(it);
    return {
      draggable: true,
      onDragStart: handleDragStart(id),
      onDragOver: handleDragOver(id),
      onDragEnd: handleDragEnd,
      onDrop: handleDrop(id),
      "data-dragging": dragId === id ? "true" : undefined,
      "data-dragover": overId === id && dragId !== id ? "true" : undefined,
    };
  }, [dragId, overId, getId, handleDragStart, handleDragOver, handleDragEnd, handleDrop]);

  return { getItemProps, dragId, overId };
}
